import { useQuery, useMutation } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { 
  AddonCatalog, 
  AddonTransaction, 
  UserAddonBalances,
} from '@shared/schema';

// Response types based on API structure
interface AddonCatalogResponse {
  success: boolean;
  addons: Array<AddonCatalog & {
    price: number; // Parsed price from decimal
  }>;
}

interface AddonBalancesResponse {
  success: boolean;
  balances: UserAddonBalances;
}

interface AddonTransactionsResponse {
  success: boolean;
  transactions: AddonTransaction[];
  pagination?: {
    page: number;
    totalPages: number;
    totalCount: number;
  };
}

interface PurchaseAddonRequest {
  items: Array<{
    sku: string;
    quantity: number;
  }>;
}

interface PurchaseAddonResponse {
  success: boolean;
  sessionId: string;
  url: string;
}

/**
 * Hook to fetch the addon catalog
 */
export function useAddonCatalog() {
  return useQuery<AddonCatalogResponse, Error>({
    queryKey: ['/api/addons/catalog'],
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch user's addon balances
 */
export function useAddonBalances() {
  return useQuery<AddonBalancesResponse, Error>({
    queryKey: ['/api/addons/balances'],
    staleTime: 30 * 1000, // 30 seconds - fresher data for balances
    refetchOnWindowFocus: true, // Refetch on window focus for real-time updates
  });
}

/**
 * Hook to fetch user's addon transaction history
 */
export function useAddonTransactions(options?: {
  page?: number;
  limit?: number;
  type?: string;
}) {
  const queryParams = new URLSearchParams();
  if (options?.page) queryParams.set('page', options.page.toString());
  if (options?.limit) queryParams.set('limit', options.limit.toString());
  if (options?.type) queryParams.set('type', options.type);
  
  const queryString = queryParams.toString();
  const url = queryString ? `/api/addons/transactions?${queryString}` : '/api/addons/transactions';
  
  return useQuery<AddonTransactionsResponse, Error>({
    queryKey: ['/api/addons/transactions', options],
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to purchase addons via Stripe Checkout
 */
export function usePurchaseAddon() {
  const { toast } = useToast();
  
  return useMutation<PurchaseAddonResponse, Error, PurchaseAddonRequest>({
    mutationFn: async (request: PurchaseAddonRequest) => {
      const response = await apiRequest('POST', '/api/addons/checkout', request);
      const data = await response.json();
      
      if (!data.success || !data.sessionId || !data.url) {
        throw new Error(data.error || 'Failed to create checkout session');
      }
      
      return data;
    },
    onSuccess: (data) => {
      // Store session ID for post-purchase validation
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('pending_addon_purchase', data.sessionId);
      }
      
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    },
    onError: (error) => {
      console.error('Purchase failed:', error);
      toast({
        title: 'Purchase Failed',
        description: error.message || 'Unable to process your purchase. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Helper hook to invalidate addon-related queries after successful purchases
 */
export function useInvalidateAddonQueries() {
  const invalidateQueries = () => {
    // Invalidate balances and transactions to reflect new purchase
    queryClient.invalidateQueries({ queryKey: ['/api/addons/balances'] });
    queryClient.invalidateQueries({ queryKey: ['/api/addons/transactions'] });
    
    // Remove pending purchase from session storage
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('pending_addon_purchase');
    }
  };
  
  return { invalidateQueries };
}

/**
 * Hook to handle post-purchase validation and cache invalidation
 */
export function usePostPurchaseHandler() {
  const { invalidateQueries } = useInvalidateAddonQueries();
  const { toast } = useToast();
  
  const handlePostPurchase = (searchParams: URLSearchParams) => {
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    
    if (success === 'true' && sessionId) {
      // Validate that this was a pending purchase
      const pendingSessionId = typeof window !== 'undefined' 
        ? sessionStorage.getItem('pending_addon_purchase')
        : null;
      
      if (pendingSessionId === sessionId) {
        // Successful purchase - invalidate queries and show success message
        invalidateQueries();
        
        toast({
          title: 'Purchase Successful!',
          description: 'Your add-ons have been added to your account.',
        });
        
        // Clean up URL parameters
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  };
  
  return { handlePostPurchase };
}

/**
 * Helper function to format addon amounts for display
 */
export function formatAddonAmount(addon: AddonCatalog): string {
  if (addon.type === 'storage') {
    if (addon.amountInt === null) {
      return 'Unlimited';
    }
    return `${addon.amountInt}GB`;
  }
  
  if (addon.type === 'illustrations') {
    return `${addon.amountInt} credits`;
  }
  
  return addon.amountInt?.toString() || 'N/A';
}

/**
 * Helper function to get usage status based on current usage and balance
 */
export function getUsageStatus(used: number, available: number): {
  status: 'full' | 'good' | 'warning' | 'critical';
  percentage: number;
} {
  const percentage = available > 0 ? (used / available) * 100 : 100;
  
  let status: 'full' | 'good' | 'warning' | 'critical';
  if (percentage >= 100) {
    status = 'full';
  } else if (percentage >= 80) {
    status = 'critical';
  } else if (percentage >= 60) {
    status = 'warning';
  } else {
    status = 'good';
  }
  
  return { status, percentage: Math.min(percentage, 100) };
}